import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { NAV_LINKS } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';
import { HOME_CONTENT } from '../lib/localizedContent';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { language, setLanguage } = useLanguage();
  const labels = HOME_CONTENT[language].nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const transparent = pathname === '/' && !scrolled;
  const tone = transparent ? 'text-bone' : 'text-charcoal';
  const linkClass = `text-[11px] tracking-[0.18em] uppercase hover:opacity-55 transition-opacity ${tone} whitespace-nowrap`;

  return (
    <motion.header
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${transparent ? 'bg-transparent' : 'bg-bone/95 backdrop-blur-md'}`}
      data-testid="site-header"
    >
      <div className="relative h-20 md:h-24 px-6 md:px-10 lg:px-14 flex items-center">
        <a
          href="/"
          className="inline-flex items-center whitespace-nowrap"
          data-testid="logo-link"
          aria-label="DIMIRXSON"
        >
          <img
            src="/logo.svg"
            alt="DIMIRXSON"
            className={`h-12 sm:h-14 md:h-8 lg:h-20 w-auto transition-all duration-500 ${transparent ? 'invert brightness-0' : ''}`}
          />
        </a>

        <nav className="hidden xl:flex absolute left-1/2 -translate-x-1/2 items-center gap-4 lg:gap-7" data-testid="primary-nav">
          {NAV_LINKS.map((link, index) => (
            <a key={link.href} href={link.href} className={linkClass}>{labels[index]}</a>
          ))}
        </nav>

        <div className="hidden xl:flex ml-auto items-center">
          <div className={`flex items-center gap-1 rounded-full border p-1 text-[10px] tracking-[0.12em] transition-colors ${transparent ? 'border-bone/50 bg-black/15 text-bone' : 'border-charcoal/20 bg-white text-charcoal'}`} data-testid="language-selector">
            {['en', 'ro'].map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLanguage(code)}
                className={`rounded-full px-3 py-2 transition-colors ${language === code ? (transparent ? 'bg-bone text-charcoal' : 'bg-charcoal text-bone') : 'opacity-60 hover:opacity-100'}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="xl:hidden ml-auto flex items-center gap-4">
          <div className={`flex gap-2 text-[10px] tracking-widest ${tone}`}>
            {['en', 'ro'].map((code) => (
              <button key={code} type="button" onClick={() => setLanguage(code)} className={language === code ? 'border-b border-current' : 'opacity-50'}>{code.toUpperCase()}</button>
            ))}
          </div>
          <button type="button" aria-label="Toggle menu" onClick={() => setOpen((value) => !value)} className={tone}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden overflow-hidden bg-bone text-charcoal px-6 py-8 border-t border-charcoal/15 flex flex-col gap-5"
          >
            {NAV_LINKS.map((link, index) => (
              <a key={link.href} href={link.href} onClick={() => setOpen(false)} className="font-sans text-3xl">{labels[index]}</a>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
